'use client';

import Script from 'next/script';

import { useEffect, useState } from 'react';

import { ADOBE_SESSION_ID_KEY } from '@/src/components/Adobe/constants';
import {
    createUUID,
    useAdTargeting,
    useBrowseContext,
    useLocalForage,
} from '@nxt-costco-com/forge-digital-components';

import { IdentityProps } from './types';

/**
 * Client component that loads the Adobe alloy script.
 *
 * Works both inside and outside of SearchProvider:
 * - On search pages, isAdTargetingEnabled is driven by SearchAdTargetingProvider
 *   (which reads from useSearchContext and feeds AdTargetingProvider).
 * - On non-search pages (e.g. StaticGrid / DLP), AdTargetingProvider defaults to
 *   true so the script loads without requiring a SearchProvider wrapper.
 */
const AdobeClient = ({ scriptSrc }: { scriptSrc: string }) => {
    const [isWindowAvailable, setIsWindowAvailable] = useState(false);
    const { thirdPartyInfo } = useBrowseContext();
    const { isAdTargetingEnabled } = useAdTargeting();
    const { setValue: setAdobeSessionId } = useLocalForage<string>(
        ADOBE_SESSION_ID_KEY,
        createUUID(),
    );

    const { consentManagement } = thirdPartyInfo;
    const { isTargetingEnabled } = consentManagement;

    const onLoad = () => {
        window.alloy('getIdentity').then(function (result: IdentityProps) {
            const adobeSessionId = result?.identity?.ECID;
            setAdobeSessionId(adobeSessionId);
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            // The Adobe script we load contains logic that will load additional scripts. Setting
            // digitalData.sitePlatform to "react" allows the script to know which additional scripts to use.
            window.digitalData = {
                ...window.digitalData,
                sitePlatform: 'react',
                pageCategories: window.digitalData?.pageCategories ?? [],
            };
            setIsWindowAvailable(true);
        }
    }, []);

    if (!isWindowAvailable || !isTargetingEnabled || !isAdTargetingEnabled) {
        return null;
    }

    return (
        <Script
            id="AdobeScript"
            onLoad={onLoad}
            src={scriptSrc}
            strategy="afterInteractive"
        />
    );
};

export { AdobeClient };
